import { useEffect, useRef } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';

const SmsListener = ({ onSmsReceived }) => {

    // Use ref to store processed SMS hashes (persists across renders)
    const processedSmsRef = useRef(new Set());

    useEffect(() => {
        let onSmsHandler = null;

        // Load processed SMS hashes from localStorage on mount
        try {
            const stored = localStorage.getItem('processedSmsHashes');
            if (stored) {
                processedSmsRef.current = new Set(JSON.parse(stored));
                console.log(`Loaded ${processedSmsRef.current.size} processed SMS hashes`);
            }
        } catch (e) {
            console.error('Failed to load processed SMS hashes:', e);
        }

        const onDeviceReady = async () => {
            console.log("Device Ready Event Fired");

            // Try to find the plugin
            const SmsReceive = (window.cordova && window.cordova.plugins && (window.cordova.plugins.SmsReceive || window.cordova.plugins.SMSReceive))
                || window.SmsReceive;

            if (SmsReceive) {
                console.log("Initializing SMS Listener...");

                try {
                    // Create Channel
                    await LocalNotifications.createChannel({
                        id: 'sms_transactions',
                        name: 'SMS Transactions',
                        description: 'Notifications for new financial SMS',
                        importance: 5,
                        visibility: 1,
                        vibration: true
                    });

                    // Request Permissions
                    await LocalNotifications.requestPermissions();
                } catch (e) {
                    console.error("Setup Error:", e);
                }

                // Start watching
                SmsReceive.startWatch(
                    function () {
                        console.log("SMS Watcher started successfully");
                    },
                    function (err) {
                        console.warn("Failed to start SMS Watcher", err);
                    }
                );

                // Define event handler with deduplication
                onSmsHandler = async (e) => {
                    console.log("📨 SMS Received Event:", e);

                    if (e && e.body) {
                        // CREATE UNIQUE HASH: sender + body + timestamp
                        // This handles:
                        // - Dual-SIM duplicates (same message, different SIM)
                        // - Multiple broadcast receivers
                        // - App restart re-delivery
                        const smsHash = `${e.address || 'unknown'}_${e.body}_${e.date || Date.now()}`;

                        // Check if already processed
                        if (processedSmsRef.current.has(smsHash)) {
                            console.log(`⚠️ Duplicate SMS detected (hash: ${smsHash.substring(0, 50)}...), skipping`);
                            return; // Skip duplicate
                        }

                        // Mark as processed
                        processedSmsRef.current.add(smsHash);

                        // Persist to localStorage (keep last 500 hashes)
                        try {
                            const hashArray = Array.from(processedSmsRef.current);
                            if (hashArray.length > 500) {
                                // Remove oldest (first 100)
                                processedSmsRef.current = new Set(hashArray.slice(100));
                            }
                            localStorage.setItem('processedSmsHashes', JSON.stringify(Array.from(processedSmsRef.current)));
                        } catch (e) {
                            console.error('Failed to save processed SMS hashes:', e);
                        }

                        try {
                            const notifId = Math.floor(Date.now() / 1000);
                            await LocalNotifications.schedule({
                                notifications: [{
                                    title: "New SMS Transaction",
                                    body: "Tap to review: " + e.body.substring(0, 50) + "...",
                                    id: notifId,
                                    channelId: 'sms_transactions',
                                    schedule: { at: new Date(Date.now() + 100) },
                                    extra: null
                                }]
                            });
                        } catch (err) {
                            console.error("Notification error:", err);
                        }

                        onSmsReceived(e);
                    }
                };

                document.addEventListener('onSMSArrive', onSmsHandler);

            } else {
                console.log("Plugin missing");
            }
        };

        if (!window.cordova) {
            setTimeout(onDeviceReady, 1000);
        } else {
            document.addEventListener('deviceready', onDeviceReady, false);
        }

        return () => {
            document.removeEventListener('deviceready', onDeviceReady);
            if (onSmsHandler) {
                document.removeEventListener('onSMSArrive', onSmsHandler);
            }
        };
    }, [onSmsReceived]);

    return null;
};

export default SmsListener;
